// Meta Configuration for all pages
// This file contains all SEO meta information for the website
import { getOGImageUrl, getRegisterUrl } from '../utils/environmentUtils';

export const metaConfig = {
  // Home/Landing Page
  '/': {
    title: "AI Jewelry Photo Shoot & Product Photos for Jewelry | Glammify",
    description: "Create stunning AI-powered jewelry photo shoots & product images instantly. Save time, reduce costs, and boost sales with professional jewelry photos using Glammify.",
    keywords: "AI jewellery model, jewellery model images, jewellery branding, jewellery visuals, jewellery marketing."
  },

  // Website Pages
  '/pricing': {
    title: "Glammify Pricing | Affordable AI Jewelry Photo Shoot Plans",
    description: "Explore flexible pricing plans for AI-generated jewelry photo shoots & images. Scale your jewelry photo needs and business with Glammify.",
    keywords: "AI jewellery model pricing, jewellery visuals pricing, Glammify pricing."
  },

  '/contact-us': {
    title: "Contact Glammify | Get Support & Business Inquiries",
    description: "Have questions or need support? Contact the Glammify team for assistance, demos, or partnership opportunities.",
    keywords: "Glammify contact, AI jewellery model support, AI jewellery photography help, Glammify Mumbai office, Glammify customer support."
  },

  '/privacy-policy': {
    title: "Privacy Policy | Glammify AI Data Protection & Security",
    description: "Learn how Glammify protects your data and ensures privacy while using AI-powered product image solutions.",
    keywords: "Glammify privacy policy, AI jewelry platform privacy, security policy, Glammify terms."
  },

  '/terms-conditions': {
    title: "Terms & Conditions | Glammify AI Platform Policies",
    description: "Read Glammify’s terms and conditions to understand platform usage, policies, and user responsibilities.",
    keywords: "Glammify terms of service, terms and conditions, product visualization policies, user agreement."
  },

  '/faqs': {
    title: "Glammify FAQs | AI Jewelry Photo Shoot Questions Answered",
    description: "Find answers about AI jewelry photo shoots, product modeling, features, pricing, and usage. Learn how Glammify creates high-quality jewelry photos easily.",
    keywords: "Glammify FAQ, AI product modeling help, Glammify pricing queries, AI jewellery photography support, Glammify tokens."
  },

  // Merchant Pages
  '/login': {
    title: "Login to Glammify | Access Your AI Dashboard",
    description: "Login to your Glammify account to create AI-powered jewelry visuals and manage your projects.",
    keywords: "Glammify login, Glammify account access, AI jewellery model, jewellery product visuals, AI jewellery photography, jewellery branding tools."
  },

  '/register': {
    title: "Glammify Sign Up | Create Your AI Jewellery Modeling Account & Get Free Tokens",
    description: "Register on Glammify to transform your jewellery, apparel & accessory images with AI-powered modeling. Sign up today — free tokens are added once your account is verified.",
    keywords: "Glammify sign up, Glammify registration, AI jewellery model sign up",
    // Open Graph meta tags for social media sharing
    // Note: Static OG tags in index.html handle the base case
    // Dynamic OG tags are handled by useDynamicOGTags hook for referral codes
    ogTitle: "Join Glammify | Create Professional AI Jewellery Model Images",
    ogDescription: "Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too!",
    ogImage: getOGImageUrl(), // Environment-aware OG image URL
    ogUrl: getRegisterUrl(), // Environment-aware register URL
    ogType: "website",
    // Twitter Card meta tags
    twitterCard: "summary_large_image",
    twitterTitle: "Join Glammify | Create Professional AI Jewellery Model Images",
    twitterDescription: "Hi! I'm using Glammify to create professional model photos of my jewellery instantly. You should try it too!",
    twitterImage: getOGImageUrl() // Environment-aware Twitter image URL
  },

  '/forgot-password': {
    title: "Forgot Password | Reset Your Glammify Account Access",
    description: "Reset your Glammify account password securely. Enter your registered email to get back access and continue creating stunning AI jewellery model images with ease.",
    keywords: "Glammify forgot password, Glammify account, Glammify login help"
  },

  '/dashboard': {
    title: "Glammify Dashboard | Manage Your AI Jewellery Model Images",
    description: "Access your Glammify dashboard to manage your AI jewellery model images, product visuals, and branding tools with secure and seamless access.",
    keywords: "Glammify dashboard, Glammify account access, AI jewellery model, jewellery product visuals, AI jewellery photography, jewellery branding tools."
  },

  '/verify-email': {
    title: "Verify Your Email | Glammify",
    description: "Verify your email to access your Glammify account and continue creating stunning AI jewellery model images with ease.",
    keywords: "Glammify verify email, Glammify account access, AI jewellery model, jewellery product visuals, AI jewellery photography, jewellery branding tools."
  },

  '/reset-password': {
    title: "Reset Your Password | Glammify",
    description: "Reset your Glammify account password securely. Enter your registered email to get back access and continue creating stunning AI jewellery model images with ease.",
    keywords: "Glammify reset password, Glammify account, Glammify login help"
  },

  '/auth/setup-password/:token': {
    title: "Setup Your Password | Glammify",
    description: "Setup your Glammify account password securely. Enter your registered email to get back access and continue creating stunning AI jewellery model images with ease.",
    keywords: "Glammify setup password, Glammify account, Glammify login help"
  },

  '/404': {
    title: "Page Not Found | Glammify",
    description: "The page you are looking for does not exist. Please check the URL and try again.",
    keywords: "Glammify 404, Glammify page not found, Glammify error"
  },

  '/500': {
    title: "Server Error | Glammify",
    description: "An error occurred on the server. Please try again later.",
    keywords: "Glammify 500, Glammify server error, Glammify error"
  },

  '/generate': {
    title: "Generate AI Jewellery Model Images | Glammify",
    description: "Generate stunning AI jewellery model images with Glammify. Upload your images and let our AI generate stunning visuals for your brand.",
    keywords: "Glammify generate, Glammify AI jewellery model, Glammify jewellery model images, Glammify jewellery branding"
  },

  '/gallery': {
    title: "AI-Generated Jewellery Gallery | Glammify",
    description: "View your gallery of AI jewellery model images and product visuals. Discover stunning visuals crafted for branding, marketing & catalogues.",
    keywords: "Glammify gallery, Glammify AI jewellery model, Glammify jewellery model images, Glammify jewellery branding"
  },

  '/history': {
    title: "Generation History | Glammify",
    description: "View your generation history with Glammify. Discover the images and videos you have generated with our AI.",
    keywords: "Glammify history, Glammify generation history, Glammify AI jewellery model, Glammify jewellery model images, Glammify jewellery branding"
  },

  '/credits': {
    title: "Manage Tokens | Glammify",
    description: "Manage your tokens with Glammify. Purchase tokens to continue using our AI jewellery model images and product visuals.",
    keywords: "Glammify tokens, Glammify AI jewellery model, Glammify jewellery model images, Glammify jewellery branding"
  },

  '/profile': {
    title: "Manage Your Profile | Glammify",
    description: "Manage your profile with Glammify. Update your profile information and settings.",
    keywords: "Glammify profile, Glammify AI jewellery model, Glammify jewellery model images, Glammify jewellery branding"
  },

  '/How-Jewellery-Brands-Can-Scale-Product-Catalogs-Without-Increasing-Photoshoot-Costs': {
    title: "How Jewellery Brands Can Scale Product Catalogs Without Increasing Photoshoot Costs | Glammify",
    description: "Learn how jewellery brands are scaling their product catalogs using AI-powered photography — without increasing photoshoot costs. Discover the power of Glammify.",
    keywords: "jewellery photoshoot cost, AI jewellery photography, jewellery product catalog, Glammify blog, scale jewellery brand"
  },

};

// Helper function to get meta data for a specific route
export function getMetaForRoute(route) {
  return metaConfig[route] || metaConfig['/']; // Default to home page if route not found
}

// Helper function to get meta data with dynamic parameters
export function getDynamicMeta(route, params = {}) {
  const baseMeta = getMetaForRoute(route);

  if (!baseMeta) return baseMeta;

  // Replace dynamic placeholders in meta data
  let dynamicMeta = { ...baseMeta };

  Object.keys(params).forEach(key => {
    const placeholder = `{${key}}`;
    if (dynamicMeta.title) {
      dynamicMeta.title = dynamicMeta.title.replace(new RegExp(placeholder, 'g'), params[key]);
    }
    if (dynamicMeta.description) {
      dynamicMeta.description = dynamicMeta.description.replace(new RegExp(placeholder, 'g'), params[key]);
    }
    if (dynamicMeta.keywords) {
      dynamicMeta.keywords = dynamicMeta.keywords.replace(new RegExp(placeholder, 'g'), params[key]);
    }
  });

  return dynamicMeta;
}

// Default meta data for fallback
export const defaultMeta = {
  title: "Glammify - AI-Powered Fashion Content Generation",
  description: "Transform your fashion business with AI-powered content creation technology.",
  keywords: "AI fashion, content generation, fashion technology, Glammify"
};
